import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, lastValueFrom, Observable} from 'rxjs';
import {CurrentContextService} from "./current-context.service";
import {UserService} from "@ui-kit/framework/security/service/user.service";
import {SessionKey} from "@ui-kit/framework/security/model/session-key.enum";
import {NavigationUtilsService} from "@ui-kit/framework/util/service/navigation-utils.service";
import {User} from "@ui-kit/framework/security/model/user.model";
import {AppConfigService} from "@ui-kit/framework/config/app-config.service";
import {BaseService} from "@ui-kit/framework/common/base.service";

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseService /*implements CurrentContext*/ {
  protected readonly env: any;
  protected loggedIn = new BehaviorSubject<boolean>(false);

  constructor(
    protected http: HttpClient,
    protected contextService: CurrentContextService<any>,
    @Inject(UserService) protected userService: UserService,
    protected utilsService: NavigationUtilsService,
    config: AppConfigService
  ) {
    super();
    this.env = config.environment;
  }

  get isLoggedIn(): Observable<boolean> {
    this.loggedIn.next(this.getToken() !== null && (this.getExpiration() > new Date()));
    return this.loggedIn.asObservable();
  }

  public setToken(res: HttpResponse<Object>): void {
    //todo: need to throw errors if the token is not correct
    const token = res.headers.get(SessionKey.TOKEN_KEY);
    if (typeof token === "string") {
      sessionStorage.setItem(SessionKey.TOKEN_KEY, token);
    }

    const tokenExpiresOn: string | null = res.headers.get('token-expires-on');
    if (typeof tokenExpiresOn === "string") {
      const expiryDate = new Date(tokenExpiresOn);
      sessionStorage.setItem(SessionKey.TOKEN_EXPIRY_KEY, expiryDate.toString());
    }

    this.loggedIn.next(true);
  }

  public doLogin(request: { email: string, secret: string }): any {
    return this.login(request)
      .then((response: HttpResponse<Object>) => {
        this.setToken(response);
        let userId = parseInt(<string>response.headers?.get('user-id'));
        this.contextService.currentUserId = userId;
        return this.lookUpAndSetUser();
      });
  }

  private login(request: { email: string, secret: string }): any {
    const uri = `${this.env.apiSecurityHost}/login`;
    let observable$ = this.http.post(uri, request, {observe: 'response'});
    return lastValueFrom(observable$)
  }

  public lookupUser(): any {
    let observable$ = this.userService.loadUser(this.contextService.currentUserId!);
    return lastValueFrom(observable$)
  }

  public logout(issueRedirect: boolean = true): void {
    const uri = `${this.env.apiSecurityHost}/logout`;
    lastValueFrom(this.http.post(uri, null, this.options))
      .catch((error: string) => {
        console.log(error);
      })
      .finally(() => {
        this.contextService.reset(true);
        this.loggedIn.next(false);
        if (issueRedirect) {
          this.utilsService.navigateWithQueryParams('/login', "Please login to continue");
        }
      });
  }

  public getExpiration(): Date {
    const expiration: string | null = sessionStorage.getItem(SessionKey.TOKEN_EXPIRY_KEY);

    return new Date((expiration ? expiration : ''));
  }

  public getToken(): string | null {
    return sessionStorage.getItem(SessionKey.TOKEN_KEY);
  }

  get isVerified(): boolean {
    return this.contextService.currentUser!.verified;
  }


  lookUpAndSetUser(): Promise<boolean> {
    return this.lookupUser()
      .then((response: Object) => {
        this.contextService.currentUser = response as User;
        return true;
      }, (error: string) => {
        console.log(error);
        return false;
      });
  }

  verifyUser(): void {
    let currentUser: User = this.contextService.currentUser!;
    currentUser.verified = true;
    this.contextService.currentUser = currentUser;
  }
}
